import React, { useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { Link } from 'react-router-dom'
import { useAtom } from 'jotai'
import { useContainerStyles } from '../../../theme/layout'
import { colors } from '../../../theme/palette'
import {
    Foa,
    Help,
    Heart,
    Aftalearkiv,
    BurgerMenu,
    Close,
} from '../../../assets/icons'
import { Typography } from '../Typography/Typography'
import { Button } from '../Button/Button'
import { AuthProfile, profileAtom } from '../../../api/profile/profile'
import { isAuthenticatedAtom, chooseImitatorAtom } from '../../../store/auth'
import { CircleIcon2 } from '../Circle/Circle'
import { LReactive as L } from '../../providers/Lokalise'
import { Menu, ILinkItem } from '../../Navigation/Menu/Menu'
import breakpoints from '../../../theme/breakpoints'
import { logout } from '../../../utils/Logout'
import { zIndexes } from '../../../theme/zIndex'
import { useWindowResize } from '../../../hooks/useWindowSize'

// Mobile menu items - extracted from Menu component
const mobileMenuLinks: ILinkItem[] = [
    {
        to: '/mine-medlemmer',
        label: 'HEADER_FANEBLAD_EGNEMEDLEMMER-TITEL',
    },
    {
        to: '/alle-foas-medlemmer',
        label: 'HEADER_FANEBLAD_ALLEMEDLEMMER-TITEL',
    },
    {
        to: '/mine-arbejdsteder',
        label: 'HEADER_FANEBLAD_MINEARBEJDSSTEDER-TITEL',
    },
    {
        to: '/mine-noter',
        label: 'HEADER_FANEBLAD_MINENOTER-TITEL',
    },
    {
        to: '/mine-opdateringer',
        label: 'HEADER_FANEBLAD_MINEOPDATERINGER-TITEL',
    },
]

if (process.env.REACT_APP_SALARY_IS_ACTIVE === 'true') {
    mobileMenuLinks.push({
        to: '/mine-lonindplaceringer',
        label: 'HEADER_FANEBLAD_LONINDPLACERINGER-TITEL',
    })
}

export const useStyles = createUseStyles((theme) => ({
    container: {
        position: 'fixed',
        backgroundColor: colors.green500,
        width: '100%',
        top: 0,
        height: 'max-content',
        borderBottom: `1px solid ${theme.palette.borderColor}`,
        zIndex: 3,
        'body.no-scroll &': {
            paddingRight: '17px',
        },
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            backgroundColor: colors.white,
        },
    },
    desktopOnly: {
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            display: 'none',
        },
    },
    mobileOnly: {
        display: 'none',
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            display: 'flex',
        },
    },
    inner: {
        height: theme.sizes.navbarDesktopHeight,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    mobileInner: {
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            height: '60px', // Smaller height for mobile
        },
    },
    brandIcon: {
        display: 'flex',
        height: '100%',
        maxWidth: '7.813rem',
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            maxWidth: '4rem', // Smaller logo on mobile
        },
    },
    containerLogin: {
        textAlign: 'right',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: colors.black,
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            display: 'none', // Hide login area on mobile
        },
    },
    mobileActions: {
        display: 'none',
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            display: 'flex',
            alignItems: 'center',
        },
    },
    burgerButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.black,
        '&:hover': {
            opacity: 0.7,
        },
    },
    mobileMenuOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100dvh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: zIndexes.drawer,
        opacity: 0,
        visibility: 'hidden',
        transition: 'opacity 0.15s ease-in-out, visibility 0.15s ease-in-out',
        '&.open': {
            opacity: 1,
            visibility: 'visible',
        },
    },
    mobileMenuContent: {
        position: 'fixed',
        top: 0,
        right: 0,
        width: '280px',
        height: '100dvh',
        backgroundColor: colors.white,
        overflowY: 'auto',
        transform: 'translateX(100%)',
        transition: 'transform 0.25s ease-out',
        zIndex: zIndexes.drawer + 1,
    },
    mobileMenuContentOpen: {
        transform: 'translateX(0)',
    },
    mobileMenuHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem',
        borderBottom: `1px solid ${colors.borderColor}`,
    },
    mobileMenuNav: {
        padding: '1rem 0',
    },
    mobileMenuLink: {
        display: 'block',
        padding: '0.75rem 1rem',
        color: colors.black,
        textDecoration: 'none',
        '&:hover': {
            backgroundColor: colors.gray500,
        },
    },
    mobileMenuActionLink: {
        display: 'flex',
        flexDirection: 'row',
        gap: '0.5rem',
        alignItems: 'center',
        padding: '0 0.5rem',
    },
    mobileMenuSection: {
        padding: '1rem',
        borderBottom: `1px solid ${colors.gray700}`,
    },
    mobileMenuActions: {
        display: 'flex',
        flexDirection: 'column',
        gap: '.1rem',
        marginTop: '1rem',
    },
    mobileMenuUser: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    profileLink: {
        textDecoration: 'underline',
        color: 'inherit',
        cursor: 'pointer',
        '&:hover, &:focus, &:active': {
            opacity: '0.8',
        },
    },
    greenText: {
        color: theme.colors.green500,
    },
    containerProfilePicture: {
        padding: '0 1rem',
        width: '80px',
    },
    logo: {
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            width: '40px',
        },
    },
    helpContainer: {
        display: 'flex',
        alignItems: 'center',
        marginRight: '30px',
        cursor: 'pointer',
    },
}))

const Navbar: React.FC = () => {
    const classes = useStyles()
    const layout = useContainerStyles()
    const [profile, _setProfile] = useAtom(profileAtom)
    const [isAuthenticated, _setIsAuthenticated] = useAtom(isAuthenticatedAtom)
    const [chooseImitator, setChooseImitator] = useAtom(chooseImitatorAtom)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const windowSize = useWindowResize()

    // Determine logo color based on screen size
    const logoColor =
        windowSize.width < breakpoints.Desktop ? colors.white : '#A6192E'

    const handleMobileMenuToggle = (): void => {
        setMobileMenuOpen(!mobileMenuOpen)
    }

    const closeMobileMenu = (): void => {
        setMobileMenuOpen(false)
    }

    const handleLogout = (): void => {
        logout()
        setMobileMenuOpen(false)
    }

    // Handle keyboard events and body scroll
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && mobileMenuOpen) {
                closeMobileMenu()
            }
        }

        const handleBodyScroll = (): void => {
            if (mobileMenuOpen) {
                document.body.style.overflow = 'hidden'
            } else {
                document.body.style.overflow = 'unset'
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        handleBodyScroll()

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.body.style.overflow = 'unset' // Clean up on unmount
        }
    }, [mobileMenuOpen])

    const changeMember = (): void => {
        const localProfileData = localStorage.getItem('profile_data')
        const data: AuthProfile = JSON.parse(localProfileData)
        data.apiProfile = undefined
        localStorage.setItem('profile_data', JSON.stringify(data))
        _setProfile(data)
        setChooseImitator(true)
        _setIsAuthenticated(false)
        setMobileMenuOpen(false) // Close menu after action
    }

    const scrollToFooter = (): void => {
        const footer = document.querySelector('#footer')
        if (footer) {
            window.scrollTo({
                top: footer.getBoundingClientRect().top,
                left: 0,
                behavior: 'smooth',
            })
        }
        setMobileMenuOpen(false) // Close menu after action
    }

    const organizeLink = (): JSX.Element => {
        return (
            <>
                {isAuthenticated || (isAuthenticated && !chooseImitator) ? (
                    <Typography
                        tag="p"
                        size="XS"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginRight: '30px',
                            cursor: 'pointer',
                        }}
                        // className={classes.profileLink}
                    >
                        <CircleIcon2 width="42px" withBorder borderWidth="6px">
                            <span
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    padding: '2px',
                                    color: colors.green,
                                }}
                            >
                                <Heart />
                            </span>
                        </CircleIcon2>

                        <Link
                            className={classes.profileLink}
                            to="/organisering"
                        >
                            Organisering
                        </Link>
                    </Typography>
                ) : null}
            </>
        )
    }

    function aftaleArkivLink(): React.ReactNode {
        return (
            <Typography
                tag="p"
                size="XS"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: '30px',
                    cursor: 'pointer',
                }}
            >
                <CircleIcon2 width="42px" withBorder borderWidth="6px">
                    <span
                        style={{
                            width: '20px',
                            height: '20px',
                            padding: '2px',
                            color: colors.green,
                        }}
                    >
                        <Aftalearkiv />
                    </span>
                </CircleIcon2>

                <a
                    className={classes.profileLink}
                    href={process.env.REACT_APP_AFTALE_ARKIV_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Aftalearkiv - åbner i nyt vindue"
                >
                    Aftalearkiv for TR
                </a>
            </Typography>
        )
    }

    return (
        <>
            <div className={classes.container}>
                <div
                    className={`${layout.container} ${classes.inner} ${classes.mobileInner}`}
                >
                    <Link className={`${classes.brandIcon}`} to="/">
                        <Foa color={logoColor} className={classes.logo} />
                    </Link>

                    {/* Desktop Navigation */}
                    <div
                        className={`${classes.containerLogin} ${classes.desktopOnly}`}
                    >
                        {aftaleArkivLink()}
                        {organizeLink()}
                        {isAuthenticated ? (
                            <Button
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginRight: '30px',
                                    cursor: 'pointer',
                                }}
                                type="action"
                                onClick={() => {
                                    scrollToFooter()
                                }}
                            >
                                <CircleIcon2
                                    width="42px"
                                    withBorder
                                    borderWidth="6px"
                                >
                                    <Help />
                                </CircleIcon2>

                                <Typography tag="p" size="XS">
                                    Hjælp
                                </Typography>
                            </Button>
                        ) : null}

                        {isAuthenticated ||
                        (!isAuthenticated && chooseImitator) ? (
                            <>
                                {isAuthenticated && (
                                    <>
                                        <Typography
                                            tag="p"
                                            size="XS"
                                            style={{ marginRight: '15px' }}
                                        >
                                            <span className={classes.greenText}>
                                                {L(
                                                    'GENERISK_HEADER_LOGINSOM_TEKST'
                                                )}
                                            </span>
                                            <Link
                                                className={classes.profileLink}
                                                to="/min-profil"
                                            >
                                                {profile?.apiProfile?.fullName}
                                            </Link>
                                        </Typography>
                                    </>
                                )}
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={logout}
                                >
                                    {L('GENERISK_HEADER_LOGUD_KNAP')}
                                </Button>
                            </>
                        ) : null}
                        {profile && profile.isImitator && isAuthenticated ? (
                            <Button
                                type="warning"
                                size="large"
                                onClick={changeMember}
                            >
                                {L('GENERISK_HEADER_SKIFTMEDLEM_KNAP')}
                            </Button>
                        ) : null}
                    </div>

                    {/* Mobile Burger Menu Button */}
                    <div className={classes.mobileActions}>
                        <button
                            type="button"
                            className={classes.burgerButton}
                            onClick={handleMobileMenuToggle}
                            aria-label="Åbn menu"
                        >
                            <BurgerMenu
                                width="24"
                                height="24"
                                fill={colors.white}
                            />
                        </button>
                    </div>
                </div>

                {/* Desktop Menu */}
                <div className={classes.desktopOnly}>
                    <Menu />
                </div>
            </div>

            {/* Mobile Menu Overlay - Dark Background */}
            <div
                className={`${classes.mobileMenuOverlay} ${
                    mobileMenuOpen ? 'open' : ''
                }`}
                onClick={closeMobileMenu}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        closeMobileMenu()
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label="Luk menu"
            />

            {/* Mobile Menu Content - Sliding Panel */}
            <div
                className={`${classes.mobileMenuContent} ${
                    mobileMenuOpen ? classes.mobileMenuContentOpen : ''
                }`}
            >
                {/* Mobile Menu Header */}
                <div className={classes.mobileMenuHeader}>
                    <Typography tag="h3" size="M" weight="bold">
                        Menu
                    </Typography>
                    <button
                        type="button"
                        className={classes.burgerButton}
                        onClick={closeMobileMenu}
                        aria-label="Luk menu"
                    >
                        <Close width="26" height="26" />
                    </button>
                </div>

                {/* Navigation Links */}
                <nav className={classes.mobileMenuNav}>
                    {mobileMenuLinks.map((link: ILinkItem) => (
                        <Link
                            key={link.to}
                            to={link.to}
                            className={classes.mobileMenuLink}
                            onClick={closeMobileMenu}
                        >
                            <Typography tag="span" size="XS" weight="bold">
                                {L(link.label)}
                            </Typography>
                        </Link>
                    ))}
                </nav>

                {/* User Section */}
                {isAuthenticated && (
                    <div className={classes.mobileMenuSection}>
                        <div className={classes.mobileMenuUser}>
                            <Typography
                                tag="p"
                                size="S"
                                mobileSize="XS"
                                weight="bold"
                            >
                                <span className={classes.greenText}>
                                    {L('GENERISK_HEADER_LOGINSOM_TEKST')}
                                </span>
                            </Typography>
                            <Link
                                className={classes.profileLink}
                                to="/min-profil"
                                onClick={closeMobileMenu}
                            >
                                <Typography tag="span" size="S" mobileSize="XS">
                                    {profile?.apiProfile?.fullName}
                                </Typography>
                            </Link>
                        </div>
                    </div>
                )}

                {/* Actions Section */}
                <div className={classes.mobileMenuActions}>
                    {/* Aftalearkiv Link */}
                    <a
                        href={process.env.REACT_APP_AFTALE_ARKIV_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.mobileMenuActionLink}
                        style={{
                            marginBottom: '0.5rem',
                            display: 'flex',
                            flexDirection: 'row',
                        }}
                    >
                        <div>
                            <CircleIcon2
                                width="42px"
                                withBorder
                                borderWidth="6px"
                            >
                                <span
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        padding: '2px',
                                        color: colors.green,
                                    }}
                                >
                                    <Aftalearkiv />
                                </span>
                            </CircleIcon2>
                        </div>
                        <div>
                            <Typography tag="span" size="XXS">
                                Aftalearkiv for TR
                            </Typography>
                        </div>
                    </a>

                    {/* Organisering Link */}
                    {(isAuthenticated ||
                        (isAuthenticated && !chooseImitator)) && (
                        <Link
                            to="/organisering"
                            className={classes.mobileMenuActionLink}
                            onClick={closeMobileMenu}
                            style={{
                                marginBottom: '0.5rem',
                                display: 'flex',
                                flexDirection: 'row',
                            }}
                        >
                            <div>
                                <CircleIcon2
                                    width="42px"
                                    withBorder
                                    borderWidth="6px"
                                >
                                    <span
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            padding: '2px',
                                            color: colors.green,
                                        }}
                                    >
                                        <Heart />
                                    </span>
                                </CircleIcon2>
                            </div>
                            <div>
                                <Typography tag="span" size="XXS">
                                    Organisering
                                </Typography>
                            </div>
                        </Link>
                    )}

                    {/* Help Button */}
                    {isAuthenticated && (
                        <button
                            type="button"
                            className={classes.mobileMenuActionLink}
                            onClick={scrollToFooter}
                            style={{
                                background: 'none',
                                border: 'none',
                                width: '100%',
                                textAlign: 'left',
                                marginBottom: '0.5rem',
                                display: 'flex',
                                flexDirection: 'row',
                            }}
                        >
                            <div>
                                <CircleIcon2
                                    width="42px"
                                    withBorder
                                    borderWidth="6px"
                                >
                                    <Help />
                                </CircleIcon2>
                            </div>
                            <div>
                                <Typography tag="span" size="XXS">
                                    Hjælp
                                </Typography>
                            </div>
                        </button>
                    )}

                    {/* Logout Button */}
                    {(isAuthenticated ||
                        (!isAuthenticated && chooseImitator)) && (
                        <Button
                            type="primary"
                            size="large"
                            onClick={handleLogout}
                            style={{ width: '95%', margin: '1rem .5rem 1rem' }}
                        >
                            {L('GENERISK_HEADER_LOGUD_KNAP')}
                        </Button>
                    )}

                    {/* Change Member Button */}
                    {profile && profile.isImitator && isAuthenticated && (
                        <Button
                            type="warning"
                            size="large"
                            onClick={changeMember}
                            style={{ width: '100%', marginTop: '0.5rem' }}
                        >
                            {L('GENERISK_HEADER_SKIFTMEDLEM_KNAP')}
                        </Button>
                    )}
                </div>
            </div>
        </>
    )
}

export { Navbar }
